import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Users, Shield, CheckCircle, XCircle, Search, Filter, User, Plus,
  FileDown, Upload, Eye, Key, TrendingUp, AlertCircle, ChevronLeft, ChevronRight,
  Save, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ClientPortalAccessGenerator from '../components/ClientPortalAccessGenerator';
import { ImportExcelDialog } from '../components/ImportExcelDialog';

/**
 * Gestión de Clientes - Vista Optimizada y Corregida
 * 
 * CORRECCIONES IMPLEMENTADAS:
 * 1. Error 1: Modal de Nuevo Cliente implementado correctamente
 * 2. Error 2: Modal de Portal con manejo correcto de cierre
 * 3. Eliminada redundancia: Solo acción [Detalle] y [Portal]
 * 4. Click en nombre del cliente navega al perfil
 * 
 * OPTIMIZACIONES:
 * - Paginación (10 clientes por página)
 * - Nueva métrica: Suma Asegurada Global
 * - Tabla simplificada
 * - Acciones consolidadas (2 iconos)
 */
export const ClientsManagementOptimized = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPortalAccess, setFilterPortalAccess] = useState('all');
  
  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(10);
  
  // Métricas
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    withPortal: 0,
    withoutPortal: 0,
    totalPolicies: 0,
    globalInsuredAmount: 0
  });
  
  // ========================================
  // CORRECCIÓN ERROR 1: MODAL DE NUEVO CLIENTE
  // ========================================
  const [newClientModalOpen, setNewClientModalOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({
    name: '',
    alias: '',
    email: '',
    phone: '',
    id_number: '',
    dob: '',
    entity_type: 'client'
  });
  const [savingNewClient, setSavingNewClient] = useState(false);
  
  // ========================================
  // CORRECCIÓN ERROR 2: MODAL DE PORTAL
  // ========================================
  const [selectedClient, setSelectedClient] = useState(null);
  const [portalConfigOpen, setPortalConfigOpen] = useState(false);
  const [portalActionSuccess, setPortalActionSuccess] = useState(false);
  
  // Importación de clientes
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchClientsAndMetrics();
  }, []);

  const fetchClientsAndMetrics = async () => {
    setLoading(true);
    
    try {
      // =========================================
      // ESTRATEGIA: PARALELISMO ROBUSTO (Promise.allSettled)
      // =========================================
      // Lanzamos ambas peticiones simultáneamente.
      // Si fallan las pólizas, NO bloqueamos la visualización de clientes.
      
      const clientsPromise = api.get('/clients?entity_type=client');
      
      // Promesa de pólizas con timeout manual para evitar bloqueos largos
      const policiesPromise = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Timeout cargando pólizas')), 8000);
        api.get('/policies')
          .then(res => { clearTimeout(timeout); resolve(res); })
          .catch(err => { clearTimeout(timeout); reject(err); });
      });

      const [clientsResult, policiesResult] = await Promise.allSettled([
        clientsPromise,
        policiesPromise
      ]);

      // =========================================
      // 1. PROCESAR CLIENTES (CRÍTICO)
      // =========================================
      let clientsData = [];
      
      if (clientsResult.status === 'fulfilled') {
        clientsData = clientsResult.value.data;
        setClients(clientsData);
      } else {
        // Si fallan los clientes, esto SÍ es un error crítico
        throw clientsResult.reason;
      }

      // =========================================
      // 2. PROCESAR PÓLIZAS (OPCIONAL / MÉTRICAS)
      // =========================================
      let policies = [];
      let totalPolicies = 0;
      let globalInsuredAmount = 0;

      if (policiesResult.status === 'fulfilled') {
        policies = policiesResult.value.data || [];
        totalPolicies = policies.length;
        
        // Calcular suma asegurada global
        globalInsuredAmount = policies.reduce((sum, p) => {
          const amount = parseFloat(p.insured_amount);
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);
      } else {
        console.warn('⚠️ Falló la carga de pólizas (Métricas secundarias afectadas):', policiesResult.reason);
        // Fallback robusto: Calcular total de pólizas sumando los contadores de cada cliente
        // Esto evita mostrar "0 pólizas" si la API de pólizas falla pero tenemos los clientes
        totalPolicies = clientsData.reduce((sum, c) => sum + (c.total_policies || 0), 0);
      }
      
      // =========================================
      // 3. ACTUALIZAR MÉTRICAS FINALES
      // =========================================
      const withPortal = clientsData.filter(c => c.portal_access?.enabled).length;
      const withoutPortal = clientsData.length - withPortal;
      
      setMetrics({
        totalClients: clientsData.length,
        withPortal,
        withoutPortal,
        totalPolicies,
        globalInsuredAmount
      });
      
      if (policiesResult.status !== 'fulfilled') {
        // Notificación discreta solo en consola, no molestamos al usuario con toast
        console.log('ℹ️ La interfaz cargó en modo de resiliencia (sin métricas detalladas de pólizas)');
      } else {
        console.log('✅ Datos cargados completamente');
      }
      
    } catch (error) {
      console.error('❌ Error crítico al cargar clientes:', error);
      
      // Solo mostramos error visible si falló lo principal (la lista de clientes)
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para ver clientes');
      } else if (error.response?.status === 500) {
        toast.error('Error en el servidor. Intente recargar.');
      } else if (error.message === 'Network Error') {
        toast.error('Error de conexión. Verifique su internet.');
      } else {
        toast.error('No se pudo cargar la lista de clientes.');
      }
      // Limpiamos estado para evitar estados inconsistentes
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // SOLUCIÓN ERROR 1: CREAR NUEVO CLIENTE
  // ========================================
  
  const handleAddClient = () => {
    // Resetear formulario
    setNewClientData({
      name: '',
      alias: '',
      email: '',
      phone: '',
      id_number: '',
      dob: '',
      entity_type: 'client'
    });
    setNewClientModalOpen(true);
  };

  const handleSaveNewClient = async () => {
    // Validación básica
    if (!newClientData.name.trim()) {
      toast.error('El nombre del cliente es obligatorio');
      return;
    }

    try {
      setSavingNewClient(true);
      
      // Llamada POST /api/clients/
      const response = await api.post('/clients', newClientData);
      
      toast.success('✅ Cliente creado exitosamente');
      setNewClientModalOpen(false);
      
      // Recargar lista de clientes
      fetchClientsAndMetrics();
      
      // Opcional: Navegar al perfil del nuevo cliente
      if (response.data.id) {
        toast.success('Redirigiendo al perfil del cliente...');
        setTimeout(() => {
          navigate(`/clients/${response.data.id}`);
        }, 1000);
      }
      
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(error.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setSavingNewClient(false);
    }
  };

  // ========================================
  // SOLUCIÓN ERROR 2: MANEJO CORRECTO DE PORTAL (Fix 3)
  // ========================================
  
  const handleConfigurePortal = (client) => {
    // Verificar si ya tiene acceso activo
    const hasAccess = client.portal_access?.enabled;
    
    // Si ya tiene acceso, mostrar modal de credenciales (o gestión)
    // Si no, abrir el generador
    setSelectedClient(client);
    setPortalActionSuccess(false);
    setPortalConfigOpen(true);
  };

  const handleClosePortalModal = () => {
    // Cerrar modal sin efectos secundarios
    setPortalConfigOpen(false);
    
    // Recargar datos SOLO si hubo una acción exitosa
    if (portalActionSuccess) {
      fetchClientsAndMetrics();
      toast.success('✅ Configuración de portal actualizada');
    }
    
    // Limpiar estados
    setTimeout(() => {
      setSelectedClient(null);
      setPortalActionSuccess(false);
    }, 300); // Delay para animación de cierre
  };

  // Esta función será llamada por el componente ClientPortalAccessGenerator cuando tenga éxito
  const handlePortalActionSuccess = () => {
    setPortalActionSuccess(true);
  };

  // ========================================
  // OPTIMIZACIÓN: NAVEGACIÓN POR CLICK EN NOMBRE
  // ========================================
  
  const handleClientClick = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  // Filtrado de clientes
  const getFilteredClients = () => {
    let filtered = clients;
    
    // Filtro por búsqueda
    if (searchTerm.trim()) {
      filtered = filtered.filter(client => {
        const search = searchTerm.toLowerCase();
        return (
          client.name?.toLowerCase().includes(search) ||
          client.email?.toLowerCase().includes(search) ||
          client.phone?.toLowerCase().includes(search)
        );
      });
    }
    
    // Filtro por portal
    if (filterPortalAccess === 'with') {
      filtered = filtered.filter(c => c.portal_access?.enabled);
    } else if (filterPortalAccess === 'without') {
      filtered = filtered.filter(c => !c.portal_access?.enabled);
    }
    
    return filtered;
  };

  // Paginación
  const filteredClients = getFilteredClients();
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-slate-500">Cargando clientes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Gestión de Clientes</h1>
          <p className="text-sm text-slate-600 mt-2">
            Administra tu cartera de clientes y sus accesos al portal
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setImportDialogOpen(true)}
            variant="outline"
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Importar
          </Button>
          <Button
            onClick={handleAddClient}
            className="gap-2 bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* ========================================= */}
      {/* MÉTRICAS CLAVE - 5 CARDS */}
      {/* ========================================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card className="border-2 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-600 uppercase">
              Total Clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-blue-700">
                {metrics.totalClients}
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-600 uppercase">
              Con Portal Activo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-green-700">
                {metrics.withPortal}
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-600 uppercase">
              Sin Portal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-orange-700">
                {metrics.withoutPortal}
              </div>
              <XCircle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-600 uppercase">
              Total Pólizas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-purple-700">
                {metrics.totalPolicies}
              </div>
              <Shield className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-slate-600 uppercase">
              Suma Asegurada Global
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-amber-700">
                {formatCurrency(metrics.globalInsuredAmount)}
              </div>
              <TrendingUp className="h-8 w-8 text-amber-500" />
            </div>
            <p className="text-xs text-amber-600 mt-1">Valor total de la cartera</p>
          </CardContent>
        </Card>
      </div>

      {/* ========================================= */}
      {/* BÚSQUEDA Y FILTROS */}
      {/* ========================================= */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Barra de búsqueda prominente */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="🔍 Buscar por nombre, email o teléfono..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 h-12 text-base"
              />
            </div>

            {/* Filtros simplificados */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  setFilterPortalAccess('all');
                  setCurrentPage(1);
                }}
                variant={filterPortalAccess === 'all' ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                Todos ({metrics.totalClients})
              </Button>
              <Button
                onClick={() => {
                  setFilterPortalAccess('with');
                  setCurrentPage(1);
                }}
                variant={filterPortalAccess === 'with' ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Con Portal ({metrics.withPortal})
              </Button>
              <Button
                onClick={() => {
                  setFilterPortalAccess('without');
                  setCurrentPage(1);
                }}
                variant={filterPortalAccess === 'without' ? 'default' : 'outline'}
                size="sm"
                className="gap-2"
              >
                <XCircle className="h-4 w-4" />
                Sin Portal ({metrics.withoutPortal})
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ========================================= */}
      {/* TABLA OPTIMIZADA - 2 ACCIONES CONSOLIDADAS */}
      {/* ========================================= */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">
              Listado de Clientes
            </CardTitle>
            <div className="text-sm text-slate-600">
              Mostrando {indexOfFirstClient + 1} - {Math.min(indexOfLastClient, filteredClients.length)} de {filteredClients.length} clientes
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {currentClients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg mb-4">No se encontraron clientes</p>
              <Button
                onClick={handleAddClient}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Crear Primer Cliente
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Nombre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Contacto Principal
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">
                      Portal
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">
                      Total Pólizas
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Última Actualización
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {currentClients.map((client) => (
                    <tr key={client.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4">
                        <div
                          onClick={() => handleClientClick(client.id)}
                          className="cursor-pointer group"
                        >
                          <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 group-hover:underline">
                            {client.name}
                          </div>
                          {client.alias && (
                            <div className="text-xs text-slate-500 italic">
                              &quot;{client.alias}&quot;
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {client.email ? (
                          <div className="flex items-center gap-2 text-sm text-slate-700">
                            <span className="text-indigo-600">📧</span>
                            {client.email}
                          </div>
                        ) : client.phone ? (
                          <div className="flex items-center gap-2 text-sm text-slate-700">
                            <span className="text-indigo-600">📞</span>
                            {client.phone}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400 italic">No especificado</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {client.portal_access?.enabled ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            ✅
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600 border-gray-300">
                            ❌
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-sm font-medium text-slate-900">
                          {client.total_policies || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-slate-600">
                          {formatDate(client.updated_at)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {/* ========================================= */}
                        {/* ACCIONES CONSOLIDADAS: SOLO 2 ICONOS */}
                        {/* ========================================= */}
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            onClick={() => handleClientClick(client.id)}
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Ver Detalle del Cliente"
                          >
                            <User className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation(); // Fix 3: Detener propagación
                              handleConfigurePortal(client);
                            }}
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 text-indigo-600 hover:bg-indigo-50"
                            title="Gestionar Acceso al Portal"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ========================================= */}
          {/* PAGINACIÓN */}
          {/* ========================================= */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
              <div className="text-sm text-slate-600">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                
                {/* Números de página */}
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      className="w-10"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                <Button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ========================================= */}
      {/* MODAL: NUEVO CLIENTE (CORRECCIÓN ERROR 1) */}
      {/* ========================================= */}
      <Dialog open={newClientModalOpen} onOpenChange={setNewClientModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Plus className="h-5 w-5 text-indigo-600" />
              Nuevo Cliente
            </DialogTitle>
            <DialogDescription>
              Completa la información del nuevo cliente. Los campos marcados con * son obligatorios.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Nombre */}
            <div>
              <Label htmlFor="new-name">Nombre Completo *</Label>
              <Input
                id="new-name"
                value={newClientData.name}
                onChange={(e) => setNewClientData({...newClientData, name: e.target.value})}
                placeholder="Nombre completo del cliente"
              />
            </div>

            {/* Alias */}
            <div>
              <Label htmlFor="new-alias">Alias</Label>
              <Input
                id="new-alias"
                value={newClientData.alias}
                onChange={(e) => setNewClientData({...newClientData, alias: e.target.value})}
                placeholder="Apodo o nombre corto"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <Label htmlFor="new-email">Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newClientData.email}
                  onChange={(e) => setNewClientData({...newClientData, email: e.target.value})}
                  placeholder="correo@ejemplo.com"
                />
              </div>

              {/* Teléfono */}
              <div>
                <Label htmlFor="new-phone">Teléfono</Label>
                <Input
                  id="new-phone"
                  value={newClientData.phone}
                  onChange={(e) => setNewClientData({...newClientData, phone: e.target.value})}
                  placeholder="+52 (55) 1234-5678"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* RFC/DNI */}
              <div>
                <Label htmlFor="new-id">RFC/DNI</Label>
                <Input
                  id="new-id"
                  value={newClientData.id_number}
                  onChange={(e) => setNewClientData({...newClientData, id_number: e.target.value})}
                  placeholder="RFC o identificación"
                />
              </div>

              {/* Fecha de Nacimiento */}
              <div>
                <Label htmlFor="new-dob">Fecha de Nacimiento</Label>
                <Input
                  id="new-dob"
                  type="date"
                  value={newClientData.dob}
                  onChange={(e) => setNewClientData({...newClientData, dob: e.target.value})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNewClientModalOpen(false)}
              disabled={savingNewClient}
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleSaveNewClient}
              disabled={savingNewClient || !newClientData.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingNewClient ? 'Guardando...' : 'Crear Cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================= */}
      {/* MODAL: PORTAL (CORRECCIÓN ERROR 2) */}
      {/* ========================================= */}
      {selectedClient && (
        <ClientPortalAccessGenerator
          client={selectedClient}
          open={portalConfigOpen}
          onOpenChange={setPortalConfigOpen}
          onClose={handleClosePortalModal}
          onAccessCreated={handlePortalActionSuccess}
        />
      )}

      {/* Modal de importación */}
      <ImportExcelDialog 
        open={importDialogOpen} 
        onOpenChange={setImportDialogOpen}
        onSuccess={fetchClientsAndMetrics}
        title="Importar Clientes desde Excel"
        description="Carga un archivo Excel (.xlsx) con los datos de los clientes"
        downloadTemplateEndpoint="/clients/template/xlsx"
        templateFilename="plantilla_clientes.xlsx"
        uploadEndpoint="/clients/import/xlsx"
        identifierLabel="Email"
        identifierField="email"
      />
    </div>
  );
};
